import { getUserInfo } from '@fff-web/fff-utilities';
import {
  ExperimentData,
  FffAnalyticsProps,
  FilteredIdentifyProps,
  PropertiesProps,
  TrackElementProps,
  TrackEventProps,
  TrackPageLoadProps,
  TrackProps,
  TraitsProps,
} from './interfaces';
import type IdentifyProps from './interfaces/identifyProps';
import {
  analyticsIsNotInitialized,
  ctaNameIsRequired,
  elementParameterIsRequired,
  trackNameIsRequired,
} from './lib/helpers/consoleWarn';
import { isTargetingAllowed, loadCookieConsentScript } from './lib/helpers/onetrust';
import { removeTrailingSlash } from './lib/helpers/removeTrailingSlash';
import { getEnv } from './lib/utils/environment';
import {
  getActiveSession,
  getDevice,
  getGenericTrackingData,
  updateSessionActivity,
} from './lib/utils/genericTracking';
import identifyInfo from './lib/utils/identifyInfo';
import isBrowser from './lib/utils/isBrowser';
import { loadSegment } from './lib/utils/loadSegment';
import { ReferrerQueryString } from './lib/utils/referrerQueryString';
import { SKIP_TITLE_CASE } from './lib/utils/toTitleCase';
import trackInfo from './lib/utils/trackInfo';
import {
  EXPERIMENT_VIEWED,
  getUUIDfromLocalStorage,
  isExperimentRunning,
  track as trackOptimizely,
  useTrackOptimizely,
} from './lib/utils/useTrackOptimizely';

let fffAnalytics: FffAnalyticsProps;

if (isBrowser()) {
  fffAnalytics = window.fffAnalytics = window.fffAnalytics || {
    segmentToken: getEnv().segmentToken,
    userData: {},
    sessionInfo: getActiveSession(),
    init: false,
  };
}

const initialize = async (): Promise<void> => {
  if (!fffAnalytics) return;
  if (!fffAnalytics.init) fffAnalytics.init = loadSegment({ token: fffAnalytics.segmentToken });
  fffAnalytics.sessionInfo = getActiveSession();
  fffAnalytics.userData = await getUserInfo();
};

const trackPageLoad = async (pageLoadProps?: TrackPageLoadProps): Promise<void | undefined> => {
  const defaultBehavior = {
    pathName: null,
    properties: {},
    options: {},
    callback: () => null,
  };
  const { pathName, properties, options, callback } = pageLoadProps || defaultBehavior;
  if (!isBrowser() || !window.analytics) return analyticsIsNotInitialized();
  const targetingAllowed = isTargetingAllowed();
  if (!targetingAllowed) return Promise.resolve();
  fffAnalytics.sessionInfo = updateSessionActivity();
  const localStorageUserInfo = await getUserInfo();
  fffAnalytics.userData = localStorageUserInfo;
  const page = removeTrailingSlash(pathName || isBrowser() ? window.location.pathname : '');
  const trackProperties: PropertiesProps = trackInfo({
    fffAnalytics,
    localStorageUserInfo,
    properties,
  });
  return window.analytics.page(page, trackProperties, options, callback);
};

const trackEvent = async ({
  ctaName,
  properties = {},
  options = {},
  callback = () => null,
  trackName,
}: TrackEventProps): Promise<void> => {
  if (!ctaName) return ctaNameIsRequired();
  return await track({
    trackName: trackName || 'Clicked CTA',
    properties: { cta_name: ctaName, ...properties },
    options,
    callback,
  });
};

const trackElement = async ({
  elementName,
  properties = {},
  options = {},
  callback = () => null,
}: TrackElementProps): Promise<void> => {
  if (!elementName) return elementParameterIsRequired();
  return await track({
    trackName: 'Clicked Element',
    properties: { element_name: elementName, ...properties },
    options,
    callback,
  });
};

const track = async ({
  trackName,
  properties = {},
  options = {},
  callback = () => null,
  bypassConsentCheck = false,
  bypassPublicPageCheck = false,
}: TrackProps): Promise<void> => {
  if (!trackName) return trackNameIsRequired();
  if (!isBrowser() || !window.analytics) return analyticsIsNotInitialized();
  const targetingAllowed = isTargetingAllowed() || bypassConsentCheck;
  if (!targetingAllowed) return Promise.resolve();
  fffAnalytics.sessionInfo = updateSessionActivity();
  const localStorageUserInfo = await getUserInfo(bypassPublicPageCheck);
  fffAnalytics.userData = localStorageUserInfo;

  const trackProperties: PropertiesProps = trackInfo({
    fffAnalytics,
    localStorageUserInfo,
    properties,
  });
  properties.propertiesToOmit?.forEach((prop) => {
    delete trackProperties[prop];
  });
  return window.analytics.track(trackName, trackProperties, options, callback);
};

export const onAnalyticsReady = (cb: () => void, maxWait = 5000, interval = 100) => {
  let waited = 0;

  const tryRun = () => {
    if (window.analytics?.initialized) {
      cb();
      return true;
    }
    return false;
  };

  if (tryRun()) return;

  const timer = setInterval(() => {
    if (tryRun() || (waited += interval) >= maxWait) {
      clearInterval(timer);
    }
  }, interval);
};

const identify = async (identifyProps?: IdentifyProps): Promise<void> => {
  const defaultIdentifyProps = {
    userId: '',
    traits: {},
    options: {},
    callback: () => null,
    bypassPublicPageCheck: false,
  };
  const { userId, traits, options, callback, bypassPublicPageCheck } =
    identifyProps || defaultIdentifyProps;
  if (!isBrowser() || !window.analytics) return analyticsIsNotInitialized();
  const targetingAllowed = isTargetingAllowed();
  if (!targetingAllowed) return Promise.resolve();
  fffAnalytics.sessionInfo = updateSessionActivity();
  const localStorageUserInfo = await getUserInfo(bypassPublicPageCheck);
  fffAnalytics.userData = localStorageUserInfo;
  const identifyUserId = userId || localStorageUserInfo?.user?.userId;
  const uuid = localStorageUserInfo?.user?.uuid;
  const identifyProperties: FilteredIdentifyProps = identifyInfo({
    userInfo: localStorageUserInfo,
    traits: { fffUUID: uuid, ...traits },
  });

  return window.analytics.identify(identifyUserId ?? null, identifyProperties, options, callback);
};

export {
  type ExperimentData,
  EXPERIMENT_VIEWED,
  getActiveSession,
  getDevice,
  identify,
  initialize,
  ReferrerQueryString,
  SKIP_TITLE_CASE,
  track,
  trackElement,
  trackEvent,
  trackPageLoad,
  useTrackOptimizely,
  isExperimentRunning,
  trackOptimizely,
  loadCookieConsentScript,
  getGenericTrackingData,
  getUUIDfromLocalStorage,
  updateSessionActivity,
  type IdentifyProps,
  type TrackProps,
  type TraitsProps,
};
